import React from 'react';
import { X, TrendingUp, TrendingDown, Wallet, ArrowLeftRight } from 'lucide-react';
import { formatBRL } from '../lib/format';

/**
 * Selection summary card matching the original D'Pots design.
 * Props:
 *  - count: number of selected transactions
 *  - income, expense, balance: numbers
 *  - onClear: () => void
 */
export default function SelectionSummary({ count, income = 0, expense = 0, balance, transfer = 0, transferCount = 0, onClear }) {
  if (!count) return null;
  const bal = balance == null ? income - expense : balance;
  // Sem este cartão, selecionar só transferências (uma distribuição de renda,
  // por exemplo) mostrava três zeros e nenhuma informação.
  const mostrarTransferencia = transferCount > 0 || Math.abs(transfer) > 0.005;
  return (
    <div className="rounded-2xl border border-indigo-200 dark:border-indigo-700/60 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30 p-5" data-testid="selection-summary">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold text-indigo-700 dark:text-indigo-300" data-testid="selection-count">
            {count} transação(ões) selecionada(s)
          </h3>
          <p className="text-sm text-indigo-600/80 dark:text-indigo-400/80">Estatísticas das transações selecionadas</p>
        </div>
        <button onClick={onClear} data-testid="selection-clear"
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm text-sm font-medium">
          <X className="w-4 h-4" /> Limpar seleção
        </button>
      </div>
      <div className={`grid grid-cols-1 gap-4 ${mostrarTransferencia ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Card label="Total de Receitas" value={income} Icon={TrendingUp} color="text-emerald-600 dark:text-emerald-400" />
        <Card label="Total de Despesas" value={expense} Icon={TrendingDown} color="text-rose-600 dark:text-rose-400" />
        <Card label="Saldo" value={bal} Icon={Wallet} color={bal < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-indigo-700 dark:text-indigo-300'} signed />
        {mostrarTransferencia && (
          <Card label="Transferências" value={transfer} Icon={ArrowLeftRight}
            color="text-blue-600 dark:text-blue-400" testid="selection-transfer"
            note={`${transferCount} ${transferCount === 1 ? 'lançamento' : 'lançamentos'} · movem dinheiro de lugar, não alteram o saldo`} />
        )}
      </div>
    </div>
  );
}

function Card({ label, value, Icon, color, signed, note, testid }) {
  return (
    <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div className={`text-2xl md:text-3xl font-bold ${color}`}>
        {signed && value < 0 ? '-' : ''}{formatBRL(Math.abs(value))}
      </div>
      {note && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-snug">{note}</div>}
    </div>
  );
}
